<template>
    <div id="calendar">
        <div id="availabilities">
            <template v-if="!hasCalendar">
                <h2 class="text-body mb-8">{{ $t('calendar.availability.title') }}</h2>
                <p class="text-primary-80">{{ $t('calendar.availability.description') }}</p>
            </template>


            <template v-else-if="!availabilities || !availabilities.length">
                <h2 class="text-body mb-8">{{ $t('calendar.no_availabilities.title') }}</h2>
                <p class="text-neutral-grey">{{ $t('calendar.no_availabilities.description') }}</p>

                <simple-button
                    class="mt-16"
                    display="secondary"
                    look="primary"
                    @click="viewAvailabilities"
                >
                    {{ $t('calendar.no_availabilities.start') }}
                </simple-button>
            </template>


            <template v-if="availabilities && availabilities.length">
                <h2 class="text-body mb-8">{{ $t('calendar.availability.title') }}</h2>
                <p>{{ $t('calendar.availability.description') }}</p>

                <div class="flex items-center space-x-24 mt-16">
                    <simple-button class="mt-16 whitespace-nowrap" look="secondary" @click="viewAvailabilities">
                        {{ $t('calendar.availability.button.change') }}
                    </simple-button>

                    <simple-button class="mt-16 whitespace-nowrap" icon="delete" display="danger" :loading="working"
                                   @click="removeAvailabilities">
                        {{ $t('calendar.availability.button.reset') }}
                    </simple-button>
                </div>
            </template>

            <popup-modal
                v-if="showAvailabilities"
                custom-classes="w-960"
                has-close-button
                :title="$t('calendar.availability.settings.title')"
                @close="showAvailabilities = false"
            >
                <loading
                    v-if="loading"
                    class="pt-24 pl-16 justify-start"
                    size="medium"
                />

                <div v-show="!loading" class="text-black">
                    <p class="mb-24 text-center">
                        {{ $t('calendar.availability.settings.description') }}
                    </p>
                    <div id="cronofy-availability-rules" ref="cronofy_rules"></div>
                    <select-dropdown
                        v-model="duration"
                        class="w-1/4 mb-16 first-letter:uppercase"
                        :options="durationOptions"
                        :auto-translate="false"
                        @change="updateAvailability"
                    >
                        {{ $t('general.duration') }}
                    </select-dropdown>
                </div>
            </popup-modal>
        </div>
    </div>
</template>

<script>
import ConfigurationSection from './ConfigurationSection';
import * as calendar from '../services/CalendarService';
import Loading from './ui/Loading';
import PopupModal from './ui/PopupModal';
import {mapState} from 'pinia';
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../state/store';
import {useAuthenticationStore} from '../state/modules/authentication';
import User from '../models/User';
import Checkbox from './forms/Checkbox';
import SelectDropdown from './forms/SelectDropdown.vue'
import SimpleButton from './ui/SimpleButton';
import Tooltip from './navigation/Tooltip';
import SvgIcon from './ui/SvgIcon';
import {toUpperCase} from "uri-js/dist/esnext/util";
import styles from '../../../../../styles.config';
import Banner from "./ui/Banner.vue";
import moment from 'moment';


export default {
    components: {
        Banner,
        SvgIcon, Tooltip, SimpleButton, Checkbox, PopupModal, Loading, ConfigurationSection, SelectDropdown
    },
    data() {
        return {
            loading: false,
            showAvailabilities: false,
            working: false,
            durationOptions: [
                {value: 15, label: '15'},
                {value: 30, label: '30'},
                {value: 60, label: '60'},
            ],
            duration: 30,

            calendarElements: Object,
            chooseCal: this.$route.query.calendar === 'added',
            syncingCalendars: false,
            primaryCalendarId: null,
        };
    },
    computed: {
        ...mapState(useMainStore, ['locale']),
        ...mapState(useAuthenticationStore, ['currentUserId']),
        user() {
            return useRepo(User).with('calendars').with('calendar_profiles').find(this.currentUserId);
        },
        availabilities() {
            return this.user.availability_rules;
        },
        hasCalendar() {
            return this.user.calendars && this.user.calendars.length;
        },
    },
    methods: {
        toUpperCase,
        getLocale() {
            return this.locale === "se" ? "sv" : this.locale;
        },
        updateAvailability() {
            this.calendarElements.update({
                config: {
                    ...this.calendarElements.config,
                    duration: this.duration
                }
            });
        },
        viewAvailabilities() {
            this.showAvailabilities = true;
            this.loading = true;
            let self = this;

            calendar.token(['managed_availability']).then(data => {
                this.calendarElements = CronofyElements.AvailabilityRules({
                    element_token: data?.token,
                    target_id: 'cronofy-availability-rules',
                    availability_rule_id: 'work_hours',
                    authorization: {
                        redirect_uri: route('app.oauth.cronofy.callback'),
                        client_id: data.client_id,
                        scope: 'read_write',
                    },
                    config: {
                        week_start_day: 'monday',
                        start_time: '08:00',
                        end_time: '18:00',
                        duration: this.duration,
                    },
                    styles: {
                        colors: {
                            hairline: styles.colors.primary[20],
                            primary: styles.colors.primary.DEFAULT,
                            available: styles.colors.semantic.info.light,
                            availableHover: styles.colors.semantic.info.DEFAULT,
                            availableActive: styles.colors.semantic.info.DEFAULT,
                        }
                    },
                    data_center: 'de',
                    locale: this.getLocale(),
                    callback(data) {
                        if (data.notification.type === 'availability_rule_saved') {
                            self.saveAvailabilityRules(data.availability_rule.weekly_periods, data.availability_rule.tzid);
                        }
                    },
                });

                let calendarsContent = document.getElementsByClassName('AvailabilityViewer__calendars')[0];
                let subDescription = document.createElement('span');
                subDescription.innerHTML = this.$t('calendar.availability.settings.sub_description');
                subDescription.classList.add('text-black', 'text-body-sm');
                calendarsContent.appendChild(subDescription);

                this.loading = false;
            });
        },
        removeAvailabilities() {
            useRepo(User)
                .whereId(this.user.id)
                .update({ availability_rules: [] });

            this.working = true;

            calendar.deleteAvailabilityRules().then(result => {
                this.working = false;
            });
        },
        saveAvailabilityRules(rules, timeZone) {
            calendar.setAvailabilityRules({duration: this.duration, time_zone: timeZone, rules})
                .then(() => {
                    useRepo(User)
                        .whereId(this.user.id).update({
                            availability_duration: this.duration,
                            availability_rules: rules,
                            time_zone: timeZone
                        });
                });
        },
    },
    mounted() {
        this.duration = this.user.availability_duration ?? this.duration;
    },
};
</script>

<style>
.AvailabilityViewer .AvailabilityViewer__calendars {
    flex-direction: column;
    align-items: flex-start;
    padding: 0 2.4rem;
}

.AvailabilityViewer .AvailabilityViewer__calendars__selector {
}

#timezone-button {
    &:hover, &:active, &:focus {
        @apply bg-white border-primary shadow-none;
    }
}

.AR .AR__timezone-selector--option {
    &:hover, &:active, &:focus {
        @apply !bg-primary !text-white;
    }
}

.AR .AR__timezone-selector--selected, .AR .AR__timezone-selector--focused {
    @apply !bg-primary !text-white !border-primary !shadow-none;
}
</style>
